import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);
const TOKEN_KEY = "nlc_token";

export function formatApiError(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function setAuthHeader(token) {
  if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  else delete axios.defaults.headers.common["Authorization"];
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anonymous, object=authenticated
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || null);

  useEffect(() => {
    const init = async () => {
      if (!token) { setUser(false); return; }
      setAuthHeader(token);
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        setUser(data);
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
        setAuthHeader(null);
        setToken(null);
        setUser(false);
      }
    };
    init();
  }, []); // eslint-disable-line

  const persist = useCallback((data) => {
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setAuthHeader(data.access_token);
    setToken(data.access_token);
    setUser(data.user);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    persist(data);
    return data.user;
  }, [persist]);

  const register = useCallback(async (name, email, password) => {
    const { data } = await axios.post(`${API}/auth/register`, { name, email, password });
    persist(data);
    return data.user;
  }, [persist]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthHeader(null);
    setToken(null);
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, register, logout, isAdmin: user && user.role === "admin" }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
